import React, { useEffect, useState } from 'react';
import { FiSearch, FiPlus, FiCopy } from 'react-icons/fi';
import { adminApi } from '../../services/adminApi';

function AdminVouchers() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createData, setCreateData] = useState({ farmer_id: '', amount: '' });
  const [approvedFarmers, setApprovedFarmers] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (filter !== 'all') params.status = filter;
    if (searchDebounced) params.search = searchDebounced;
    adminApi.getVouchers(params)
      .then((res) => {
        if (res.success) {
          setVouchers(res.data || []);
          setError(null);
        } else setError(res.message || 'Failed to load');
      })
      .catch((err) => {
        setError(err.message || 'Failed to load');
        setVouchers([]);
      })
      .finally(() => setLoading(false));
  }, [filter, searchDebounced]);

  useEffect(() => {
    if (showCreate) {
      adminApi.getFarmers({ status: 'approved' })
        .then((res) => {
          if (res.success) setApprovedFarmers(res.data || []);
        })
        .catch(() => setApprovedFarmers([]));
    }
  }, [showCreate]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createData.farmer_id || !createData.amount) return;
    setCreating(true);
    try {
      const res = await adminApi.createVoucher({
        farmer_id: parseInt(createData.farmer_id, 10),
        amount: parseFloat(createData.amount),
      });
      if (res.success) {
        setVouchers((prev) => [res.data, ...prev]);
        setShowCreate(false);
        setCreateData({ farmer_id: '', amount: '' });
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
  };

  const formatDate = (d) => d ? new Date(d).toISOString().slice(0, 10) : '-';
  const formatAmount = (n) => n != null ? `${Number(n).toLocaleString()} FCFA` : '-';

  return (
    <div className="admin-page">
      <div className="admin-page-header admin-page-header-row">
        <div>
          <h1>Voucher Management</h1>
          <p>Generate and manage financing vouchers</p>
        </div>
        <button className="admin-btn-primary" onClick={() => setShowCreate(true)}>
          <FiPlus />
          Generate Voucher
        </button>
      </div>
      {showCreate && (
        <div className="admin-modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Generate Voucher</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Farmer</label>
                <select
                  value={createData.farmer_id}
                  onChange={(e) => setCreateData((p) => ({ ...p, farmer_id: e.target.value }))}
                  required
                >
                  <option value="">Select farmer</option>
                  {approvedFarmers.map((f) => (
                    <option key={f.id} value={f.id}>{f.full_name} (F{String(f.id).padStart(3, '0')})</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Amount (FCFA)</label>
                <input
                  type="number"
                  min="1"
                  value={createData.amount}
                  onChange={(e) => setCreateData((p) => ({ ...p, amount: e.target.value }))}
                  placeholder="e.g. 50000"
                  required
                />
              </div>
              <div className="admin-modal-actions">
                <button type="button" className="admin-btn-outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn-primary" disabled={creating}>
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <div className="admin-toolbar">
        <div className="admin-search">
          <FiSearch className="admin-search-icon" />
          <input
            type="search"
            placeholder="Search vouchers..."
            className="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="admin-filter-tabs">
          {['all', 'pending', 'active', 'redeemed'].map((tab) => (
            <button
              key={tab}
              className={`admin-filter-tab ${filter === tab ? 'active' : ''}`}
              onClick={() => setFilter(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="admin-table-card">
        {loading ? (
          <div className="admin-loading" />
        ) : error ? (
          <p className="admin-error">{error}</p>
        ) : vouchers.length === 0 ? (
          <p className="admin-empty">No vouchers found</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Voucher ID</th>
                <th>Farmer</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Issue Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vouchers.map((v) => (
                <tr key={v.id}>
                  <td>
                    <span className="admin-voucher-cell">
                      <code>{v.code}</code>
                      <button
                        className="admin-btn-icon admin-btn-icon-sm"
                        title="Copy"
                        onClick={() => copyCode(v.code)}
                      >
                        <FiCopy />
                      </button>
                    </span>
                  </td>
                  <td>{v.farmer_name || '-'}</td>
                  <td>{formatAmount(v.amount)}</td>
                  <td>
                    <span className={`admin-badge admin-badge-${v.status}`}>
                      {v.status}
                    </span>
                  </td>
                  <td>{formatDate(v.created_at)}</td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminVouchers;
